/**
 * The dead end a CLIENT sees when a code chunk never arrives and one reload
 * did not fix it. Their event company speaks, not STAFFED: a bare system
 * error on the screen where someone was about to pay reads as "the payment
 * system is broken". With no tenant branding available it falls back to plain
 * text — never a STAFFED logo, marketing copy or a sign-in form.
 */
import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { loadTenantBrand } from '../lib/tenantBrand';
import { hardRefresh } from '../lib/chunkRecovery';

export default function ClientLoadFailure() {
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    let live = true;
    loadTenantBrand().then((b) => { if (live) setBrand(b); });
    return () => { live = false; };
  }, []);

  const company = (brand?.companyName || '').trim();

  return (
    <div
      data-testid="client-load-failure"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        background: '#0B1020',
        color: '#E9ECF5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
        overflowY: 'auto',
      }}
    >
      <div style={{ maxWidth: 460, width: '100%', textAlign: 'left' }}>
        {brand?.companyLogo ? (
          <img
            src={brand.companyLogo}
            alt={company || 'Event company'}
            data-testid="client-load-failure-logo"
            style={{ height: 44, width: 'auto', maxWidth: 240, display: 'block', marginBottom: 22 }}
          />
        ) : company ? (
          <div
            data-testid="client-load-failure-company"
            style={{ fontSize: 15, fontWeight: 600, letterSpacing: 0.2, marginBottom: 22 }}
          >
            {company}
          </div>
        ) : null}

        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
          This page didn&apos;t load fully
        </h1>
        <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.6, color: 'rgba(233,236,245,0.78)' }}>
          Part of this page didn&apos;t download, so some of it is missing. Please try again, or
          reopen the link from the email{company ? ` ${company} sent you` : ' your event company sent you'}.
        </p>

        <button
          type="button"
          onClick={hardRefresh}
          data-testid="client-load-failure-retry"
          style={{
            marginTop: 26,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#F4F6FB',
            color: '#0B1020',
            border: 0,
            borderRadius: 9999,
            padding: '11px 22px',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <RefreshCw style={{ width: 15, height: 15 }} />
          Try again
        </button>

        <p
          data-testid="client-load-failure-contact"
          style={{ margin: '22px 0 0', fontSize: 13, lineHeight: 1.6, color: 'rgba(233,236,245,0.55)' }}
        >
          {company
            ? `Still not loading? Reply to the email from ${company} and they'll send you a fresh link.`
            : "Still not loading? Reply to the email your event company sent you and they'll send you a fresh link."}
        </p>
      </div>
    </div>
  );
}
